import { useState, useEffect, startTransition } from 'react';
import { useStore } from '~/context/store-context';
import {
  STORE_REGIONS,
  getRegionByStoreValue,
  getRegionDisplayName,
  type RegionData,
  type StoreLocation,
} from '~/constants/regions';
import {
  safeGetRegionByStoreValue,
  safeGetRegionDisplayName,
  safeRegionPrioritization,
  logRegionValidation,
} from '~/utils/region-error-handling';

export interface UseRegionsOptions {
  autoExpandUserRegion?: boolean;
  defaultExpanded?: boolean;
}

export interface UseRegionsReturn {
  regions: Record<string, RegionData>;
  expandedRegions: Record<string, boolean>;
  toggleRegion: (regionKey: string) => void;
  setRegionExpanded: (regionKey: string, expanded: boolean) => void;
  userRegion: string | null;
  getUserRegionDisplayName: () => string | null;
}

/**
 * Reusable hook for managing regional store data and expansion state
 * Used by Header, Mobile Sidebar, and future Store Selector components
 */
export const useRegions = (options: UseRegionsOptions = {}): UseRegionsReturn => {
  const { autoExpandUserRegion = false, defaultExpanded = false } = options;
  const { selectedStore } = useStore();
  const [isHydrated, setIsHydrated] = useState(false);

  // Initialize expanded state for all regions
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    Object.keys(STORE_REGIONS).forEach((regionKey) => {
      initial[regionKey] = defaultExpanded;
    });
    // Add "Other Locations" for responsive layouts
    initial['other-locations'] = defaultExpanded;
    return initial;
  });

  // Mark as hydrated after component mounts and validate region data in dev
  useEffect(() => {
    setIsHydrated(true);

    // Validate region data in development
    if (process.env.NODE_ENV === 'development') {
      logRegionValidation();
    }
  }, []);

  // Determine user's region based on selected store (with error handling)
  const userRegionKey = safeGetRegionByStoreValue(selectedStore);
  const userRegion = userRegionKey ? safeGetRegionDisplayName(userRegionKey) : null;

  // Auto-expand user's region if enabled - only after hydration
  useEffect(() => {
    if (isHydrated && autoExpandUserRegion && userRegion) {
      startTransition(() => {
        setExpandedRegions((prev) => ({
          ...prev,
          [userRegion]: true,
        }));
      });
    }
  }, [isHydrated, autoExpandUserRegion, userRegion]);

  const toggleRegion = (regionKey: string) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionKey]: !prev[regionKey],
    }));
  };

  const setRegionExpanded = (regionKey: string, expanded: boolean) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionKey]: expanded,
    }));
  };

  const getUserRegionDisplayName = (): string | null => {
    return userRegion ? getRegionDisplayName(userRegion) : null;
  };

  return {
    regions: STORE_REGIONS,
    expandedRegions,
    toggleRegion,
    setRegionExpanded,
    userRegion,
    getUserRegionDisplayName,
  };
};

/**
 * Responsive region logic for different screen sizes with smart user prioritization
 * Returns regions grouped appropriately for the given width, with user's region prioritized
 * Handles SSR/hydration safely by deferring prioritization until after hydration
 */
export const getResponsiveRegions = (
  windowWidth: number,
  isClient: boolean = true,
  userRegionKey?: string | null,
  isHydrated: boolean = true,
): Array<[string, StoreLocation[] | { name: string; locations: StoreLocation[] }]> => {
  const allRegions = Object.entries(STORE_REGIONS);

  // During SSR or before client hydration, show default order to prevent hydration mismatches
  // Only apply user prioritization after hydration is complete
  const shouldPrioritize = isClient && isHydrated && userRegionKey;

  const prioritizedRegions = shouldPrioritize
    ? safeRegionPrioritization(allRegions, userRegionKey)
    : allRegions;

  // For SSR consistency, use default window width if not client-side
  const effectiveWidth = isClient ? windowWidth : 1280;

  if (effectiveWidth >= 1280) {
    // XL: Show all 6 regions (with user's region first if applicable)
    // Long Beach, Los Angeles, Orange County, Inland Empire/San Diego, Central Coast, Northern California
    return prioritizedRegions.map(([key, region]) => [region.displayName, region.stores]);
  } else if (effectiveWidth >= 1024) {
    // LG: Show 3 regions + "Other Locations" (user's region guaranteed to be visible if prioritized)
    // Prioritize: Long Beach, Los Angeles, Orange County + Other Locations
    const visibleRegions = prioritizedRegions.slice(0, 3);
    const otherRegions = prioritizedRegions.slice(3);

    return [
      ...visibleRegions.map(
        ([key, region]) => [region.displayName, region.stores] as [string, StoreLocation[]],
      ),
      [
        'Other Locations',
        {
          name: 'Other Locations',
          locations: otherRegions.flatMap(([key, region]) => region.stores),
        },
      ],
    ];
  } else {
    // MD: Show 2 regions + "Other Locations" (user's region guaranteed to be visible if prioritized)
    // Prioritize: Long Beach, Los Angeles + Other Locations
    const visibleRegions = prioritizedRegions.slice(0, 2);
    const otherRegions = prioritizedRegions.slice(2);

    return [
      ...visibleRegions.map(
        ([key, region]) => [region.displayName, region.stores] as [string, StoreLocation[]],
      ),
      [
        'Other Locations',
        {
          name: 'Other Locations',
          locations: otherRegions.flatMap(([key, region]) => region.stores),
        },
      ],
    ];
  }
};
